import { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { Doughnut } from 'react-chartjs-2';

import { getLatestNotification } from 'redux/actions';
import { DOUGHNUT_OPTIONS } from './constants';
import { ALERT_PROTO_TYPES } from 'utils/constants';
import { CircleIcon } from 'components/icons';
import { calcPercent } from 'utils/helpers';
import { useAsyncAction } from 'hooks';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '100%',
    width: '100%',
  },
  footer: {
    width: '100%',
    marginTop: theme.spacing(4),
  },
  chart: {
    marginTop: theme.spacing(3),
    position: 'relative',
    height: 'fit-content',
  },
  chartLabel: {
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
  },
  sent: {
    fontFamily: theme.custom.fonts.SFUITextSemibold,
    fontSize: 36,
    color: '#394659',
  },
  total: {
    fontFamily: theme.custom.fonts.SFUITextRegular,
    fontSize: 16,
    color: '#cbd2d5',
  },
  label: {
    fontFamily: theme.custom.fonts.SFUITextMedium,
    fontSize: 14,
    color: '#333',
    marginLeft: theme.spacing(3),
  },
  title: {
    fontFamily: theme.custom.fonts.SFUITextMedium,
    fontSize: 16,
    color: '#333',
    overflowWrap: 'break-word',
  },
}));

const chartColors = [
  '#00748c',
  '#f24024',
  '#01a1c3',
  '#ffc659',
  '#825dde',
  '#2eb100',
  '#392902',
];

const responsesWithCounts = (survey) => {
  const responses = survey?.surveyResponses || [];
  const answers = survey?.surveyAnswers;
  if (!Array.isArray(answers) || answers.length === 0) {
    return responses;
  }

  const tallies = responses.map(() => 0);
  answers.forEach((ans) => {
    const idx = Number(ans?.answer);
    if (Number.isInteger(idx) && idx >= 0 && idx < tallies.length) {
      tallies[idx] += 1;
    }
  });

  return responses.map((response, idx) => ({
    ...response,
    count: Math.max(Number(response.count) || 0, tallies[idx]),
  }));
};

const LatestAlert = () => {
  const classes = useStyles();
  const { selectedGame } = useSelector((state) => state.games);
  const isFutureGame = useMemo(
    () => !selectedGame || new Date(selectedGame.startTime) > new Date(),
    [selectedGame]
  );
  const latestSurvey = useSelector(
    (state) => state.notifications.latestSurvey
  );
  const responses = useMemo(
    () => responsesWithCounts(latestSurvey),
    [latestSurvey]
  );
  const sent = latestSurvey?.sent || 0;
  const title = latestSurvey?.title || '';

  const totalCount = useMemo(
    () => responses.reduce((acc, { count = 0 }) => acc + count, 0),
    [responses]
  );

  const responsePercent = useMemo(
    () =>
      responses
        .map(({ count = 0 }) => calcPercent(count, sent))
        .concat(calcPercent(sent - totalCount, sent)),
    [responses, totalCount, sent]
  );

  const labels = useMemo(
    () => responses.map(({ response }) => response).concat('No Response'),
    [responses]
  );

  const chartData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          data: isFutureGame ? [] : responsePercent,
          backgroundColor: chartColors,
        },
      ],
    }),
    [labels, responsePercent, isFutureGame]
  );

  useAsyncAction(
    getLatestNotification(ALERT_PROTO_TYPES.survey, selectedGame?.id),
    true,
    selectedGame?.id
  );

  return (
    <Card className={classes.root}>
      <CardHeader title={'Latest Quick Poll'} />
      <CardContent>
        <div className={classes.container}>
          <div className={classes.chart}>
            <div className={classes.chartLabel}>
              <Typography
                variant="body2"
                color="textPrimary"
                className={classes.sent}
              >
                {isFutureGame ? 0 : sent}
              </Typography>
              <Typography
                variant="body2"
                color="textPrimary"
                className={classes.total}
              >
                Sent
              </Typography>
            </div>
            <Doughnut
              data={chartData}
              options={DOUGHNUT_OPTIONS}
              width={200}
              height={200}
            />
          </div>
          {!isFutureGame && sent > 0 && (
            <div className={classes.footer}>
              <Box mb={2}>
                <Typography className={classes.title}>{title}</Typography>
              </Box>
              {labels.map((label, idx) => (
                <Box key={idx} display="flex" alignItems="center" mb={1}>
                  <CircleIcon color={chartColors[idx % chartColors.length]} />
                  <Typography className={classes.label}>
                    {label} ({responsePercent[idx]}%)
                  </Typography>
                </Box>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default memo(LatestAlert);
